import {ChartAggOption} from './../../../util/plotHelpers/chartTypes';
import {
  buildAreaPoint,
  buildNonFinitePoint,
  buildPoint,
  buildTimePoint,
  extendPoint,
  type PointWithMeta,
} from './buildPoints';
import {HistoryRecord} from './types';
import {getSafeXKey, getSafeYKey} from './util';

type ParseFnMeta = {
  isFiniteX: boolean;
  isFiniteY: boolean;
  hasHiddenNaN: boolean;
};

export const makeParseHistoryPoint = (
  finitePoints: PointWithMeta[],
  nonFinitePoints: PointWithMeta[],
  config: {
    agg: 'Avg' | 'minMax';
    groupAgg?: ChartAggOption;
    metric: string;
    startTime: number;
    usingExpressions: boolean;
    xMetric: string;
    xMetrics: string[];
  }
) => {
  /**
   * This function is used to parse a history point into a point that can be plotted on the graph. The API can return the following non-finites as strings as part of a metric's history: NaN, Infinity, -Infinity
   * The API can also return time data [needs documentation]
   * 
   * Data: the API returns history points that look like this
   * type HistoryRecord = {
   *   bucketSize: number;
   *   [xMetric]Avg|Min|Max: number | 'Infinity' | '-Infinity' | 'NaN';
   *   [xMetric]HasNaN: boolean;
   *   [metric]Avg|Min|Max: number | 'Infinity' | '-Infinity' | 'NaN';
   *   [metric]HasNaN: boolean; 
   * }
   * 
   * We make two lines out of these points for full fidelity:
   * 1. A primary line (agg = "Avg") that is made of x/y points on the avg values
   * 2. A secondary line (agg = "minMax") that is made of x/y/y0 points on the min/max values

   * 
   * # Primary Line Cases:
   * 1a. neither x or y values are non-finite => 
   *   - translate x/y pairs 
   *   - push resulting points into finitePoints list
   * 1b. x values include non-finites => 
   *   - ???
   * 1c. y values include non-finites =>  
   *   - translate x/y pairs
   *   - push resulting points into nonFinitePoints list
   * 1d. x/y values include non-finites =>
   *   - ???
   * 1e. The HistoryRecord will return a `[metric]HasNaN` key if the metric has a NaN value in it.
   *   - Map this value against the x-axis value and push it into the non-finite list
   * 
   * # Secondary line cases:
   * react-vis specifies that y0 points should be the minimums, but in our implementation y0 has always been the maximum. This doesn't seem to matter but there was a weird behavior related to this when doing min/max line aggregations in grouping [needs exploration]
   * 
   * 2a. neither x or y or y0 values are non-finite => 
   *   - translate x/y/0 pairs 
   *   - push resulting points into finitePoints list
   * 2b. y or y0 values include Inf/-Inf => 
   *   - these values (min/max) can only include Inf/-Inf as values, NaN is not returned by the backend as a possible value in a min/max point
   *   - construct points converting the Inf/-Inf from strings to JS Inf
   *   - push these points into the finitePoints list
   *   - NOTE: later on will we handle setting them to the maximum range of the chart
   * 2c. Ignore `[metric]HasNaN` fields here since the primary line will cover this
   * 
   */
  const {agg, groupAgg, metric, startTime, xMetric, xMetrics} = config;
  const safeXKey = getSafeXKey(xMetric, metric, 'last');
  const safeYKey = getSafeYKey(metric, undefined);
  const isAvgPoint = agg.toLowerCase() === 'avg';

  function preParsePoint(historyPoint: HistoryRecord) {
    /**
     * If we're doing absolute time plotting we need to extend the points with the computed value for _absolute_runtime
     * see notes: frontends/app/src/components/PanelRunsLinePlot/timePlots/readme.md
     */
    if (xMetric === '_absolute_runtime') {
      buildTimePoint(historyPoint, startTime);
    }

    const meta = {
      isFiniteX: isFinite(historyPoint[safeXKey] as number),
      isFiniteY: isFinite(historyPoint[safeYKey] as number),
      hasHiddenNaN: Boolean(historyPoint[`${metric}HasNaN`]),
    };

    return isAvgPoint
      ? parseAvgHistoryPoint(historyPoint, meta)
      : parseAreaHistoryPoint(historyPoint);
  }

  function parseAvgHistoryPoint(
    historyPoint: HistoryRecord,
    meta: ParseFnMeta
  ) {
    const pointBuilder = meta.isFiniteY ? buildPoint : buildNonFinitePoint;
    const list = meta.isFiniteY ? finitePoints : nonFinitePoints;
    /**
     * We need to split finite from non-finite points. Non-finite points come in from the API as strings instead of numbers (but our type system doesn't expect this currently). Sampled rendering splits non-finite numbers out into `nanPoints` as distinct from data, so that we can render a discrete series of points that aren't plottable. We do the same thing here.
     *
     * If we're plotting the average line we throw non-finite values into the `nanPoints` array so we can render them as distinct from the data
     *
     * If it's an area plot NaN gets excluded from min/max/avg
     */
    if (!meta.isFiniteX) {
      // case 1b, 1d
      // we just drop these values since they're not plottable
    } else {
      // handle primary line points
      // construct a point for the value
      // covers case 1a and 1c because pointBuilder switches based on isFiniteY
      const point = pointBuilder(
        historyPoint,
        xMetric,
        metric,
        groupAgg,
        false
      );
      if (config.usingExpressions) {
        extendPoint(point, historyPoint, xMetrics, groupAgg);
      }

      list.push(point);

      // Construct an additional point for the NaN value if it exists
      // case 1e
      if (meta.hasHiddenNaN) {
        nonFinitePoints.push(
          buildNonFinitePoint(historyPoint, xMetric, metric, groupAgg, true)
        );
      }
    }
  }

  function parseAreaHistoryPoint(historyPoint: HistoryRecord) {
    try {
      const [point, minPoint, maxPoint] = buildAreaPoint(
        historyPoint,
        xMetric,
        metric
      );

      if (config.usingExpressions) {
        extendPoint(point, historyPoint, xMetrics, groupAgg);
      }
      finitePoints.push(point);
      nonFinitePoints.push(
        ...([minPoint, maxPoint].filter(Boolean) as PointWithMeta[])
      );
    } catch (e) {
      // if for some reason a NaN shows up where it shouldn't be log that
      // we don't want to throw here because that kicks us out of full fidelity with the error boundary. Should update this in the future because we will wnat to know if this happens w/out crashing the user experience
      console.warn(e);
    }
  }

  return preParsePoint;
};
